import {
  getCategoryLevelsFromHierarchy,
  getProductCategoryPath,
} from "./categoryPeerImages";

export const UNCATEGORIZED_SALES_SLUG = "__uncategorized__";

const collectCategoryMeta = (nodes, map = new Map(), order = []) => {
  if (!Array.isArray(nodes)) return { map, order };

  nodes.forEach((node) => {
    if (node?.slug && !map.has(node.slug)) {
      map.set(node.slug, {
        id: node.id,
        slug: node.slug,
        name: node.name,
      });
      order.push(node.slug);
    }
    if (node?.children?.length) {
      collectCategoryMeta(node.children, map, order);
    }
  });

  return { map, order };
};

const extractSalesProducts = (source) => {
  if (Array.isArray(source)) return source;
  if (source?.products?.length) return source.products;

  const nested = [];
  const visit = (node) => {
    if (!node) return;
    if (Array.isArray(node)) {
      node.forEach(visit);
      return;
    }
    if (node.products?.length) nested.push(...node.products);
    (node.children || []).forEach(visit);
  };
  visit(source);
  return nested;
};

const compareByCatalogOrder = (a, b, orderIndex) => {
  if (a === b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  const ia = orderIndex.has(a) ? orderIndex.get(a) : Number.MAX_SAFE_INTEGER;
  const ib = orderIndex.has(b) ? orderIndex.get(b) : Number.MAX_SAFE_INTEGER;
  if (ia !== ib) return ia - ib;
  return String(a).localeCompare(String(b), "he");
};

const departmentNameFromProducts = (entries, slug) => {
  for (const { product } of entries) {
    const match = getProductCategoryPath(product).find(
      (cat) => cat.slug === slug,
    );
    if (match?.name) return match.name;
  }
  return null;
};

/** Group a flat sales product list into department sections (catalog order). */
export const groupSalesProductsByDepartment = (source, categoryTree) => {
  const products = extractSalesProducts(source);
  if (!products.length) return source;

  const entries = products.map((product, index) => ({
    product,
    index,
    levels: getCategoryLevelsFromHierarchy(getProductCategoryPath(product)),
  }));

  if (!entries.some((entry) => entry.levels.department)) return source;

  const { map: categoryMeta, order: categoryOrder } =
    collectCategoryMeta(categoryTree);
  const orderIndex = new Map(categoryOrder.map((slug, i) => [slug, i]));

  const buckets = new Map();
  entries.forEach((entry) => {
    const departmentSlug = entry.levels.department || UNCATEGORIZED_SALES_SLUG;
    if (!buckets.has(departmentSlug)) buckets.set(departmentSlug, []);
    buckets.get(departmentSlug).push(entry);
  });

  const departmentSlugs = [...buckets.keys()].sort((a, b) => {
    if (a === UNCATEGORIZED_SALES_SLUG) return 1;
    if (b === UNCATEGORIZED_SALES_SLUG) return -1;
    return compareByCatalogOrder(a, b, orderIndex);
  });

  const children = departmentSlugs.map((slug) => {
    const departmentEntries = buckets.get(slug);
    const sortedProducts = [...departmentEntries]
      .sort((a, b) => {
        const groupCmp = compareByCatalogOrder(
          a.levels.group,
          b.levels.group,
          orderIndex,
        );
        if (groupCmp !== 0) return groupCmp;
        const subgroupCmp = compareByCatalogOrder(
          a.levels.subgroup,
          b.levels.subgroup,
          orderIndex,
        );
        if (subgroupCmp !== 0) return subgroupCmp;
        return a.index - b.index;
      })
      .map((entry) => entry.product);

    const meta = categoryMeta.get(slug);

    return {
      id: meta?.id ?? slug,
      slug,
      name:
        slug === UNCATEGORIZED_SALES_SLUG
          ? "ללא מחלקה"
          : meta?.name ||
            departmentNameFromProducts(departmentEntries, slug) ||
            slug,
      parentSlug: "_root",
      isFamily: false,
      products: sortedProducts,
      children: [],
    };
  });

  const root = Array.isArray(source) ? {} : { ...source };
  return {
    ...root,
    products: [],
    isFamily: false,
    children,
  };
};
